// ---------------------------------------------------------------------------
// DSS-11: Full border controls — style, width, color, radius, per-side link
// ---------------------------------------------------------------------------
import { useCallback, useId } from "react";
import { Frame, Link, Unlink } from "lucide-react";
import { normalizeColorInputValue } from "./color-input-value";

// ── Types ──
export type BorderStyle = "none" | "solid" | "dashed" | "dotted" | "double" | "groove" | "ridge";

export interface BorderConfig {
  readonly style: BorderStyle;
  readonly widthTop: number;
  readonly widthRight: number;
  readonly widthBottom: number;
  readonly widthLeft: number;
  readonly isWidthLinked: boolean;
  readonly color: string;
  readonly radiusTopLeft: number;
  readonly radiusTopRight: number;
  readonly radiusBottomRight: number;
  readonly radiusBottomLeft: number;
  readonly isRadiusLinked: boolean;
}

interface BorderControlsProps {
  readonly value: BorderConfig;
  readonly onChange: (config: BorderConfig) => void;
}

// ── Constants ──
const BORDER_STYLES: BorderStyle[] = [
  "none", "solid", "dashed", "dotted", "double", "groove", "ridge",
];
const MIN_WIDTH = 0;
const MAX_WIDTH = 20;
const MIN_RADIUS = 0;
const MAX_RADIUS = 200;

type WidthSide = "widthTop" | "widthRight" | "widthBottom" | "widthLeft";
type RadiusCorner = "radiusTopLeft" | "radiusTopRight" | "radiusBottomRight" | "radiusBottomLeft";

const WIDTH_SIDES: { key: WidthSide; label: string }[] = [
  { key: "widthTop", label: "T" },
  { key: "widthRight", label: "R" },
  { key: "widthBottom", label: "B" },
  { key: "widthLeft", label: "L" },
];

const RADIUS_CORNERS: { key: RadiusCorner; label: string }[] = [
  { key: "radiusTopLeft", label: "TL" },
  { key: "radiusTopRight", label: "TR" },
  { key: "radiusBottomRight", label: "BR" },
  { key: "radiusBottomLeft", label: "BL" },
];

// ── NaN guard ──
function clamp(raw: string, min: number, max: number, fallback: number): number {
  const parsed = parseFloat(raw);
  if (Number.isNaN(parsed)) return fallback;
  return Math.min(Math.max(parsed, min), max);
}

// ── Component ──
export default function BorderControls({ value, onChange }: BorderControlsProps) {
  const uid = useId();

  const update = useCallback(
    (patch: Partial<BorderConfig>) => {
      onChange({ ...value, ...patch });
    },
    [value, onChange],
  );

  const updateWidth = useCallback(
    (side: WidthSide, raw: string) => {
      const val = clamp(raw, MIN_WIDTH, MAX_WIDTH, value[side]);
      if (value.isWidthLinked) {
        update({
          widthTop: val, widthRight: val,
          widthBottom: val, widthLeft: val,
        });
      } else {
        update({ [side]: val });
      }
    },
    [value, update],
  );

  const updateRadius = useCallback(
    (corner: RadiusCorner, raw: string) => {
      const val = clamp(raw, MIN_RADIUS, MAX_RADIUS, value[corner]);
      if (value.isRadiusLinked) {
        update({
          radiusTopLeft: val, radiusTopRight: val,
          radiusBottomRight: val, radiusBottomLeft: val,
        });
      } else {
        update({ [corner]: val });
      }
    },
    [value, update],
  );

  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center gap-2 text-xs font-semibold text-[var(--color-foreground)]">
        <Frame className="h-3.5 w-3.5" />
        Border
      </div>

      <div className="flex flex-col gap-2.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/40 p-3">
        {/* Border style */}
        <div className="flex items-center gap-2">
          <label
            htmlFor={`${uid}-style`}
            className="w-16 shrink-0 text-[10px] font-medium text-[var(--color-muted-foreground)]"
          >
            Style
          </label>
          <select
            id={`${uid}-style`}
            value={value.style}
            onChange={(e) => update({ style: e.target.value as BorderStyle })}
            className="h-7 flex-1 rounded-md border border-[var(--color-border)] bg-[var(--color-card)] px-2 text-[11px] capitalize text-[var(--color-foreground)]"
          >
            {BORDER_STYLES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        {/* Border color */}
        <div className="flex items-center gap-2">
          <label
            htmlFor={`${uid}-color`}
            className="w-16 shrink-0 text-[10px] font-medium text-[var(--color-muted-foreground)]"
          >
            Color
          </label>
          <input
            id={`${uid}-color`}
            type="color"
            value={normalizeColorInputValue(value.color)}
            onChange={(e) => update({ color: e.target.value })}
            className="h-6 w-8 cursor-pointer rounded border border-[var(--color-border)] bg-transparent p-0"
          />
          <span className="text-[10px] tabular-nums text-[var(--color-muted-foreground)]">
            {value.color}
          </span>
        </div>

        {/* Border width per-side */}
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-medium text-[var(--color-muted-foreground)]">
              Width (px)
            </span>
            <button
              type="button"
              onClick={() => update({ isWidthLinked: !value.isWidthLinked })}
              title={value.isWidthLinked ? "Unlink sides" : "Link all sides"}
              className={[
                "rounded p-1",
                value.isWidthLinked
                  ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                  : "text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]",
              ].join(" ")}
            >
              {value.isWidthLinked ? <Link className="h-3 w-3" /> : <Unlink className="h-3 w-3" />}
            </button>
          </div>
          <div className="grid grid-cols-4 gap-1.5">
            {WIDTH_SIDES.map(({ key, label }) => (
              <div key={key} className="flex flex-col items-center gap-0.5">
                <span className="text-[8px] uppercase text-[var(--color-muted-foreground)]">
                  {label}
                </span>
                <input
                  type="number"
                  min={MIN_WIDTH}
                  max={MAX_WIDTH}
                  value={value[key]}
                  onChange={(e) => updateWidth(key, e.target.value)}
                  className="h-6 w-full rounded border border-[var(--color-border)] bg-[var(--color-card)] text-center text-[10px] tabular-nums text-[var(--color-foreground)]"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Border radius per-corner */}
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-medium text-[var(--color-muted-foreground)]">
              Radius (px)
            </span>
            <button
              type="button"
              onClick={() => update({ isRadiusLinked: !value.isRadiusLinked })}
              title={value.isRadiusLinked ? "Unlink corners" : "Link all corners"}
              className={[
                "rounded p-1",
                value.isRadiusLinked
                  ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                  : "text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]",
              ].join(" ")}
            >
              {value.isRadiusLinked ? <Link className="h-3 w-3" /> : <Unlink className="h-3 w-3" />}
            </button>
          </div>
          <div className="grid grid-cols-4 gap-1.5">
            {RADIUS_CORNERS.map(({ key, label }) => (
              <div key={key} className="flex flex-col items-center gap-0.5">
                <span className="text-[8px] uppercase text-[var(--color-muted-foreground)]">
                  {label}
                </span>
                <input
                  type="number"
                  min={MIN_RADIUS}
                  max={MAX_RADIUS}
                  value={value[key]}
                  onChange={(e) => updateRadius(key, e.target.value)}
                  className="h-6 w-full rounded border border-[var(--color-border)] bg-[var(--color-card)] text-center text-[10px] tabular-nums text-[var(--color-foreground)]"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Live preview */}
        <div className="flex items-center justify-center py-2">
          <div
            className="h-16 w-24 bg-[var(--color-card)]"
            style={{
              borderStyle: value.style,
              borderColor: value.color,
              borderTopWidth: `${value.widthTop}px`,
              borderRightWidth: `${value.widthRight}px`,
              borderBottomWidth: `${value.widthBottom}px`,
              borderLeftWidth: `${value.widthLeft}px`,
              borderTopLeftRadius: `${value.radiusTopLeft}px`,
              borderTopRightRadius: `${value.radiusTopRight}px`,
              borderBottomRightRadius: `${value.radiusBottomRight}px`,
              borderBottomLeftRadius: `${value.radiusBottomLeft}px`,
            }}
          />
        </div>
      </div>

      {/* CSS output */}
      <div className="rounded-md bg-[var(--color-muted)] px-3 py-2">
        <code className="whitespace-pre-wrap text-[10px] text-[var(--color-muted-foreground)]">
          {generateBorderCss(value)}
        </code>
      </div>
    </div>
  );
}

// ── CSS generation ──
export function generateBorderCss(config: BorderConfig): string {
  if (config.style === "none") return "border: none;";

  const lines: string[] = [];
  const { widthTop: wt, widthRight: wr, widthBottom: wb, widthLeft: wl } = config;
  const allWidthSame = wt === wr && wr === wb && wb === wl;

  if (allWidthSame) {
    lines.push(`border: ${wt}px ${config.style} ${config.color};`);
  } else {
    lines.push(`border-style: ${config.style};`);
    lines.push(`border-color: ${config.color};`);
    lines.push(`border-width: ${wt}px ${wr}px ${wb}px ${wl}px;`);
  }

  const { radiusTopLeft: rtl, radiusTopRight: rtr, radiusBottomRight: rbr, radiusBottomLeft: rbl } = config;
  const allRadiusSame = rtl === rtr && rtr === rbr && rbr === rbl;

  if (allRadiusSame && rtl > 0) {
    lines.push(`border-radius: ${rtl}px;`);
  } else if (!allRadiusSame) {
    lines.push(`border-radius: ${rtl}px ${rtr}px ${rbr}px ${rbl}px;`);
  }

  return lines.join("\n");
}
